import React from 'react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import axios from 'axios';

const ModifyHourSubmittal = ({ verification, onComplete, onError }) => {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const abortControllerRef = React.useRef(null);

  // Helper function to convert Uint8Array to base64
  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  const generatePDF = async () => {
    // Prevent multiple simultaneous generations
    if (isGenerating) {
      console.log('PDF generation already in progress, skipping duplicate call');
      return;
    }

    setIsGenerating(true);
    abortControllerRef.current = new AbortController();

    try {
      console.log('Starting PDF generation for registration:', verification.filename);
      
      // Fetch the existing PDF document from S3
      const url = 'https://acuitycte-files.s3.us-west-2.amazonaws.com/public/pdf-templates/Student-Verification-Form.pdf';
      const template_response = await fetch(url, { signal: abortControllerRef.current.signal });
      if (!template_response.ok) throw new Error('Failed to fetch PDF template');
      const existingPdfBytes = await template_response.arrayBuffer();

      // Load and modify the PDF
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const instructorSignature = await pdfDoc.embedPng(verification.instructorSignature);
      const page1 = pdfDoc.getPages()[0];

      // Modify the PDF with verification data
      page1.drawText("Event Title: " + verification.eventTitle, {
        x: 60,
        y: 560,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      page1.drawRectangle({
        x: 58,
        y: 536,
        width: 250,
        height: 18,
        color: rgb(1, 1, 1),
      });
      page1.drawRectangle({
        x: 56,
        y: 465,
        width: 250,
        height: 18,
        color: rgb(1, 1, 1),
      });
      page1.drawText(verification.eventDate, {
        x: 60,
        y: 465,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      page1.drawText(verification.studentName, {
        x: 90,
        y: 590,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      page1.drawText(verification.hours.toString(), {
        x: 90,
        y: 384,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      page1.drawText(verification.instructorName, {
        x: 90,
        y: 298,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      page1.drawImage(instructorSignature, {
        x: 90,
        y: 210,
        width: 250,
        height: 15,
      });
      page1.drawRectangle({
        x: 200,
        y: 75,
        width: 250,
        height: 60,
        color: rgb(1, 1, 1),
      });
      page1.drawText(verification.verifierName, {
        x: 90,
        y: 197,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      
      const job = verification.job;
      page1.drawText("Enrolled Concentration: " + verification.concentration, {
        x: 50,
        y: 150,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      page1.drawText("Job Title: " + job.title, {
        x: 50,
        y: 120,
        size: 12,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });
      
      const jobConcentrations =
        job.otherConcentrations && job.otherConcentrations.length > 0
          ? job.concentration.title + " with " + job.otherConcentrations.join(", ")
          : job.concentration.title;
      
      page1.drawText(
        "Job Concentrations: " + jobConcentrations + "\nJob Description: " + job.description,
        {
          x: 50,
          y: 105,
          size: 10,
          lineHeight: 11,
          maxWidth: 500,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        }
      );

      // Save the modified PDF as base64
      const pdfBytes = await pdfDoc.save();
      const base64String = arrayBufferToBase64(pdfBytes);

      // Send to the backend
      const response = await axios.post('/api/save-pdf',
        {
          location: verification.folderLocation,
          filename: verification.filename,
          pdffile: base64String,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          signal: abortControllerRef.current.signal
        }
      );

      console.log('PDF generation completed successfully:', response.data.url);
      
      // Return the PDF URL for the parent component
      onComplete(response.data.url);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('PDF generation was aborted');
        return;
      }
      console.error('Error generating PDF:', error);
      onError(error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate PDF immediately when component mounts
  React.useEffect(() => {
    generatePDF();
    
    // Cleanup function to abort any ongoing requests when component unmounts
    return () => {
      if (abortControllerRef.current) {
        console.log('Aborting PDF generation due to component unmount');
        abortControllerRef.current.abort();
      }
    };
  }, []); // Empty dependency array ensures this only runs once

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0, 0, 0, 0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{ 
        backgroundColor: '#fff', 
        padding: '2rem', 
        borderRadius: '8px', 
        textAlign: 'center',
        maxWidth: '400px'
      }}>
        <h3>Generating Verification Document...</h3>
        <p>Please wait while we create your PDF verification document.</p>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3', 
          borderTop: '4px solid #1D4E89', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite',
          margin: '1rem auto'
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ModifyHourSubmittal; 